import { elementsOutline, mapElementPoints, roundCoordinate, shiftElements, type DiagramElement, type DiagramPitch } from '@ukinfo/shared';
import { PITCH_HEIGHT } from './PitchDiagram';

/**
 * Spiegeln und Drehen von Elementen der Skizze - reine Rechnung ohne React.
 *
 * Gerechnet wird in Zeichnungseinheiten (Breite 100, Hoehe `PITCH_HEIGHT`), nicht in Prozent: Ein Quadrat bleibt auch
 * auf dem ganzen Feld (Hoehe rund 154) ein Quadrat, wenn es um 90 Grad gedreht wird. Das Ergebnis liegt wieder auf dem
 * Raster der Koordinaten (Vielfache von 0,5). Spiegeln und 180 Grad bleiben im Umriss der Elemente; nach 90 Grad
 * tauschen Breite und Hoehe, die Gruppe wird dann ins Feld geschoben - passt sie nicht hinein, gibt es einen Hinweis.
 */

export type TransformOp = 'flipH' | 'flipV' | 'rot90' | 'rot180';

export const TRANSFORM_ACTIONS: ReadonlyArray<{ op: TransformOp; short: string; label: string }> = [
  { op: 'flipH', short: 'Waagrecht spiegeln', label: 'Waagrecht spiegeln' },
  { op: 'flipV', short: 'Senkrecht spiegeln', label: 'Senkrecht spiegeln' },
  { op: 'rot90', short: '90° drehen', label: '90° drehen' },
  { op: 'rot180', short: '180° drehen', label: '180° drehen' },
];

export type TransformResult =
  | {
      ok: true;
      elements: DiagramElement[];
      /** Die Gruppe war gedreht groesser als der Platz und wurde ins Feld geschoben. */
      shifted: boolean;
      /** Nichts hat sich geaendert (z. B. ein Spieler ohne Blickrichtung, der auf der Stelle gespiegelt wird). */
      unchanged: boolean;
    }
  | { ok: false; problem: string };

/** Drehwinkel (Grad, im Uhrzeigersinn ab oben) nach der Operation. */
function angleAfter(angle: number, op: TransformOp): number {
  const value = op === 'flipH' ? -angle : op === 'flipV' ? 180 - angle : op === 'rot90' ? angle + 90 : angle + 180;
  return ((value % 360) + 360) % 360;
}

/** Drehung einer Huerde: eine Linie, die nur auf 180 Grad ankommt (Bar quer zur Laufrichtung). */
function barAfter(angle: number, op: TransformOp): number {
  const value = op === 'flipH' || op === 'flipV' ? -angle : op === 'rot90' ? angle + 90 : angle;
  return ((value % 180) + 180) % 180;
}

/** Dreht, was am Element selbst gedreht ist (nicht seine Lage): Blickrichtung, Tor, Huerde, Spitze eines Dreiecks. */
function turnElement(element: DiagramElement, op: TransformOp): DiagramElement {
  switch (element.type) {
    case 'player': {
      if (element.facing === undefined) return element;
      return { ...element, facing: angleAfter(element.facing, op) };
    }
    case 'goal': {
      const rotation = angleAfter(element.rotation ?? 0, op) as 0 | 90 | 180 | 270;
      return element.rotation === undefined && rotation === 0 ? element : { ...element, rotation };
    }
    case 'hurdle': {
      const rotation = barAfter(element.rotation ?? 0, op) as 0 | 45 | 90 | 135;
      return element.rotation === undefined && rotation === 0 ? element : { ...element, rotation };
    }
    case 'zone': {
      if (element.shape !== 'triangle') return element;
      const rotation = angleAfter(element.rotation ?? 0, op) as 0 | 90 | 180 | 270;
      const { rotation: _alt, ...rest } = element;
      return rotation === 0 ? rest : { ...rest, rotation };
    }
    default:
      return element;
  }
}

/**
 * Spiegelt oder dreht die Elemente `indices` (ohne Angabe oder leer: die ganze Skizze) um die Mitte ihres Umrisses -
 * bei der ganzen Skizze um die Feldmitte. Text bleibt lesbar: nur seine Lage aendert sich. Pfeile, Kurven, Knickpunkte,
 * Masslinien und Zonen laufen mit; Zonen tauschen bei 90 Grad Breite und Hoehe (in Zeichnungseinheiten).
 */
export function transformElements(elements: readonly DiagramElement[], indices: readonly number[] | null, op: TransformOp, pitch: DiagramPitch): TransformResult {
  const whole = indices === null || indices.length === 0;
  const targets = whole ? elements.map((_, index) => index) : [...new Set(indices)].filter((index) => elements[index]);
  if (targets.length === 0) return { ok: false, problem: 'Es gibt nichts zu spiegeln oder zu drehen.' };

  const height = PITCH_HEIGHT[pitch];
  const picked = targets.map((index) => elements[index]!);
  const outline = elementsOutline(picked)!;
  // Mitte in Prozent der Breite (x) und der Hoehe (y); `cyUnits` dieselbe Mitte in Zeichnungseinheiten.
  const cx = whole ? 50 : (outline.minX + outline.maxX) / 2;
  const cy = whole ? 50 : (outline.minY + outline.maxY) / 2;
  const cyUnits = (cy * height) / 100;

  const move = (x: number, y: number): [number, number] => {
    const dx = x - cx;
    const dy = (y * height) / 100 - cyUnits;
    const [nx, ny] = op === 'flipH' ? [-dx, dy] : op === 'flipV' ? [dx, -dy] : op === 'rot90' ? [-dy, dx] : [-dx, -dy];
    return [roundCoordinate(cx + nx), roundCoordinate(((cyUnits + ny) * 100) / height)];
  };

  let turned = picked.map((element) => turnElement(mapElementPoints(element, move), op));

  // Nach 90 Grad tauschen Breite und Hoehe: die Gruppe muss ins Feld passen, sonst wird sie hineingeschoben.
  const after = elementsOutline(turned)!;
  if (after.maxX - after.minX > 100 || after.maxY - after.minY > 100) {
    const scope = whole ? 'Die ganze Skizze' : 'Die Auswahl';
    return {
      ok: false,
      problem: `${scope} passt gedreht nicht ins Feld – ${whole ? 'auf diesem Feld lässt sie sich nur um 180° drehen oder spiegeln' : 'wähle weniger Elemente oder drehe um 180°'}.`,
    };
  }
  const sx = after.minX < 0 ? -after.minX : after.maxX > 100 ? 100 - after.maxX : 0;
  const sy = after.minY < 0 ? -after.minY : after.maxY > 100 ? 100 - after.maxY : 0;
  const shifted = sx !== 0 || sy !== 0;
  if (shifted) turned = shiftElements(turned, sx, sy);

  const next = [...elements];
  targets.forEach((index, position) => {
    next[index] = turned[position]!;
  });
  const unchanged = JSON.stringify(next) === JSON.stringify(elements);
  return { ok: true, elements: unchanged ? (elements as DiagramElement[]) : next, shifted, unchanged };
}
